import { useNavigate, useLocation, type NavigateFunction } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { Suspense, useEffect } from "react";
import routes from "./config";
import PortalLoadingSkeleton from "@/components/feature/PortalLoadingSkeleton";
import PortalScrollReset from "./PortalScrollReset";
import ErrorBoundary from "@/components/feature/ErrorBoundary";
import PortalNav from "@/components/feature/PortalNav";
import PortalFooter from "@/components/feature/PortalFooter";
import { isLoggedIn } from "@/lib/auth";
import { isRouteModuleLoadError } from "@/lib/routeModuleRecovery";

function RouteShell({ failed = false }: { failed?: boolean }) {
  return <div className="flex min-h-screen flex-col bg-brand-bg">
    {isLoggedIn() && (failed ? <PortalNav /> : <header className="fixed inset-x-0 top-0 z-40 border-b border-brand-border bg-white px-6 py-5">
      <nav aria-label="Portal loading navigation"><a href="/clientportal" className="font-semibold text-brand-dark">Portal home</a></nav>
    </header>)}
    <main className="flex-1 px-4 pb-10 pt-24"><div className="mx-auto max-w-7xl">
      {failed ? <div role="alert" className="portal-card p-6">
        <h1 className="font-fraunces text-xl">This section could not be downloaded</h1>
        <p className="mt-2">Check your connection, then try again. Your saved work is still available.</p>
        <button type="button" className="btn-primary mt-4" onClick={() => window.location.reload()}>Try again</button>
      </div> : <PortalLoadingSkeleton />}
    </div></main>
    {failed && <PortalFooter />}
  </div>;
}

function RouteFailure({ error }: { error: Error | null }) {
  if (!isRouteModuleLoadError(error)) throw error;
  return <RouteShell failed />;
}

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

// eslint-disable-next-line react-refresh/only-export-components -- existing tests and modules import this helper from the component module; keep the public API stable for this lint-only cleanup.
export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const element = useRoutes(routes);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });

  useEffect(() => {
    // History restoration must not reopen contact autofill on operational pages.
    // Dialog focus and deliberately focused entry workflows keep their semantics.
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && focused.closest('[role="dialog"], [role="alertdialog"], [autofocus]')) return;
    const previous = document.body.getAttribute('tabindex');
    document.body.setAttribute('tabindex', '-1');
    document.body.focus({preventScroll:true});
    if (previous === null) document.body.removeAttribute('tabindex');
    else document.body.setAttribute('tabindex', previous);
  }, [location.pathname]);

  // Neutral last-resort UI; route details remain in browser/server diagnostics.
  if (!element) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#F5F2EB] px-6 py-12">
        <div className="max-w-xl w-full bg-white rounded-xl border border-[#E8E2D8] p-8 text-center">
          <div className="w-14 h-14 rounded-xl bg-stone-100 border border-stone-200 flex items-center justify-center mx-auto mb-5">
            <i className="ri-map-pin-line text-stone-500 text-2xl" />
          </div>
          <h1 className="text-xl font-semibold text-stone-800 mb-2">We couldn’t load this page</h1>
          <p className="text-sm text-stone-500 mb-4">
            Please return to a portal entry page or try again.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <a href="/clientportal" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-stone-800 text-white text-xs font-medium hover:bg-stone-700 transition-colors">
              <i className="ri-user-line" /> Client Portal
            </a>
            <a href="/staff" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 text-stone-700 text-xs font-medium hover:bg-stone-50 transition-colors">
              <i className="ri-stethoscope-line" /> Staff
            </a>
            <a href="/admin" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 text-stone-700 text-xs font-medium hover:bg-stone-50 transition-colors">
              <i className="ri-settings-3-line" /> Admin
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PortalScrollReset />
      <ErrorBoundary key={location.pathname} fallback={(error) => <RouteFailure error={error} />}>
      <Suspense fallback={<RouteShell />}>
        {element}
      </Suspense>
      </ErrorBoundary>
    </>
  );
}
